export function formatVitalLabel(vitalName) {
  if (!vitalName) return "";
  const normalized = vitalName?.charAt(0).toUpperCase() + vitalName?.slice(1);
  switch (normalized) {
    case "HeartRate":
      return "Heart Rate";
    case "BloodPressure":
      return "Blood Pressure";
    case "RespiratoryRate":
      return "Respiratory Rate";
    case "Bmi":
      return "BMI";
    case "Weight":
      return "Weight";
    case "Temperature":
      return "Temperature";
    case "Height":
      return "Height";
    case "Circumference":
      return "Circumference";
    case "OxygenSaturation":
      return "Oxygen Saturation";
    case "PainScore":
      return "NEWS Pain Score";
    case "GlucoseLevel":
      return "Blood Sugar";
    case "Consciousness":
      return "Consciousness (AVPU)";
    case "SupplementalOxygen":
      return "Supplemental Oxygen";
    default:
      return vitalName;
  }
}

export function safeParsePayload(details) {
  if (!details) return details;

  if (typeof details === "string") {
    try {
      return JSON.parse(details);
    } catch {
      return details;
    }
  }

  if (details?.payload && typeof details.payload === "string") {
    try {
      const parsed = JSON.parse(details.payload);
      return parsed || details;
    } catch {
      return details;
    }
  }

  return details;
}

export function getVitalDataFromDetails(details) {
  if (!details || typeof details !== "object") return null;

  const vitalKeys = [
    "heartRate",
    "bloodPressure",
    "temperature",
    "respiratoryRate",
    "height",
    "weight",
    "circumference",
    "bmi",
    "OxygenSaturation",
    "PainScore",
    "GlucoseLevel",
    "Consciousness",
    "SupplementalOxygen",
  ];

  const foundVitals = vitalKeys.filter((key) => details[key]);
  if (foundVitals.length === 0) return null;

  if (details.recordedVitalType && details[details.recordedVitalType]) {
    const key = details.recordedVitalType;

    // Special handling for blood pressure
    if (key === "bloodPressure" && details.bloodPressure) {
      const bp = details.bloodPressure;
      const systolicValue =
        bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
      const diastolicValue =
        bp.Diastolic?.Value ||
        bp.diastolic?.Value ||
        bp.Diastolic ||
        bp.diastolic;

      return {
        vitalKey: key,
        vitalData: bp,
        vitalType: "Blood Pressure",
        displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
      };
    }

    // Special handling for height
    if (key === "height" && details.height) {
      const height = details.height;
      // Check if height is in cm and add unit
      let displayValue = height.Value || "--";
      if (height.Unit === "cm" && displayValue !== "--") {
        displayValue = `${displayValue} cm`;
      }
      return {
        vitalKey: key,
        vitalData: height,
        vitalType: "Height",
        displayValue: displayValue,
      };
    }

    return {
      vitalKey: key,
      vitalData: details[key],
      vitalType: formatVitalLabel(key),
    };
  }

  if (foundVitals.length === 1) {
    const key = foundVitals[0];

    // Special handling for blood pressure
    if (key === "bloodPressure" && details.bloodPressure) {
      const bp = details.bloodPressure;
      const systolicValue =
        bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
      const diastolicValue =
        bp.Diastolic?.Value ||
        bp.diastolic?.Value ||
        bp.Diastolic ||
        bp.diastolic;

      return {
        vitalKey: key,
        vitalData: bp,
        vitalType: "Blood Pressure",
        displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
      };
    }

    // Special handling for height
    if (key === "height" && details.height) {
      const height = details.height;
      return {
        vitalKey: key,
        vitalData: height,
        vitalType: "Height",
        displayValue: height.Value || "--",
      };
    }

    return {
      vitalKey: key,
      vitalData: details[key],
      vitalType: formatVitalLabel(key),
    };
  }

  if (foundVitals.includes("bmi")) {
    const bmi = details["bmi"];
    const weight = details["weight"];
    const height = details["height"];

    if (bmi?.Value && bmi?.Unit && (!weight?.Value || !height?.Value)) {
      return {
        vitalKey: "bmi",
        vitalData: bmi,
        vitalType: formatVitalLabel("bmi"),
      };
    }

    if (foundVitals.includes("weight")) {
      return {
        vitalKey: "weight",
        vitalData: weight,
        vitalType: formatVitalLabel("weight"),
      };
    }

    if (foundVitals.includes("height")) {
      const height = details["height"];
      return {
        vitalKey: "height",
        vitalData: height,
        vitalType: "Height",
        displayValue: height.Value || "--",
      };
    }
  }

  const key = foundVitals[0];

  // Special handling for blood pressure
  if (key === "bloodPressure" && details.bloodPressure) {
    const bp = details.bloodPressure;
    const systolicValue =
      bp.Systolic?.Value || bp.systolic?.Value || bp.Systolic || bp.systolic;
    const diastolicValue =
      bp.Diastolic?.Value ||
      bp.diastolic?.Value ||
      bp.Diastolic ||
      bp.diastolic;

    return {
      vitalKey: key,
      vitalData: bp,
      vitalType: "Blood Pressure",
      displayValue: `${systolicValue || "--"}/${diastolicValue || "--"} mmHg`,
    };
  }

  // Special handling for height
  if (key === "height" && details.height) {
    const height = details.height;
    return {
      vitalKey: key,
      vitalData: height,
      vitalType: "Height",
      displayValue: height.Value || "--",
    };
  }

  return {
    vitalKey: key,
    vitalData: details[key],
    vitalType: formatVitalLabel(key),
  };
}
